/**
 * Die Prüfbüros, die diese Anwendung benutzen.
 *
 * Das ist die Verwaltung der Anwendung selbst, nicht die eines Büros.
 * Deshalb hängt sie am Merkmal `systemverwalter` und nicht an einem Recht:
 * Rechte werden innerhalb eines Mandanten vergeben, und dort darf sich
 * niemand selbst über den eigenen Mandanten hinausheben.
 *
 * Es gibt kein Löschen. An einem Prüfbüro hängen Prüfberichte, und die
 * sind Nachweise erbrachter Leistungen mit Nummern aus einem lückenlosen
 * Kreis. Was es gibt, ist Sperren: die Anmeldung geht nicht mehr, die
 * Daten bleiben.
 */

import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  useMandantAendern,
  useMandantAnlegen,
  useMandanten,
} from '../api/mandanten'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, datumDeutsch } from '../bausteine/Hinweise'
import { useKonto } from '../konto/KontoKontext'

export function Mandanten() {
  const { konto } = useKonto()
  const mandanten = useMandanten()
  const aendern = useMandantAendern()
  const [neuOffen, setzeNeuOffen] = useState(false)

  if (konto && !konto.systemverwalter) return <Navigate to="/" replace />
  if (mandanten.isLoading) return <Ladeanzeige />
  if (mandanten.error) return <Fehlerhinweis fehler={mandanten.error} />

  const liste = mandanten.data ?? []

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">Prüfbüros</h1>
        <button
          type="button"
          className="knopf-haupt ml-auto"
          onClick={() => setzeNeuOffen(true)}
        >
          Neues Prüfbüro
        </button>
      </header>

      <p className="text-base text-slate-700">
        Die Büros, die diese Anwendung benutzen. Jedes sieht ausschließlich
        seine eigenen Kunden, Plätze und Prüfungen.
      </p>

      <Fehlerhinweis fehler={aendern.error} />

      <ul className="space-y-2">
        {liste.map((m) => (
          <li key={m.id} className={`karte ${m.aktiv ? '' : 'border-dashed opacity-70'}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">
                  {m.name}{' '}
                  <span className="font-mono text-base text-slate-600">
                    ({m.kurzform})
                  </span>
                  {!m.aktiv && (
                    <span className="ml-2 marke border-2 border-stufe-3 text-stufe-3">
                      gesperrt
                    </span>
                  )}
                </div>
                <div className="text-base text-slate-700">
                  {[m.inhaber, m.ort].filter(Boolean).join(' · ') || '—'}
                  {' · seit '}
                  {datumDeutsch(m.erstellt_am)}
                </div>
              </div>

              <button
                type="button"
                className={m.aktiv ? 'knopf-warnung px-3 text-base' : 'knopf-zweit px-3 text-base'}
                disabled={aendern.isPending}
                onClick={() => {
                  const frage = m.aktiv
                    ? `„${m.name}" sperren?\n\nDie Anmeldung geht dann nicht mehr, und ` +
                      'laufende Sitzungen werden sofort beendet. Die Daten bleiben.'
                    : `„${m.name}" wieder freigeben?`
                  if (window.confirm(frage)) {
                    aendern.mutate({ id: m.id, aktiv: !m.aktiv })
                  }
                }}
              >
                {m.aktiv ? 'Sperren' : 'Freigeben'}
              </button>
            </div>

            <dl className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-base text-slate-700">
              <div>
                <dt className="inline text-slate-600">Benutzer: </dt>
                <dd className="inline font-semibold">{m.anzahl_benutzer}</dd>
              </div>
              <div>
                <dt className="inline text-slate-600">Kunden: </dt>
                <dd className="inline font-semibold">{m.anzahl_kunden}</dd>
              </div>
              <div>
                <dt className="inline text-slate-600">Spielplätze: </dt>
                <dd className="inline font-semibold">{m.anzahl_spielplaetze}</dd>
              </div>
              <div>
                <dt className="inline text-slate-600">Prüfungen: </dt>
                <dd className="inline font-semibold">{m.anzahl_pruefungen}</dd>
              </div>
            </dl>
          </li>
        ))}
      </ul>

      {neuOffen && <NeuesBuero schliessen={() => setzeNeuOffen(false)} />}
    </div>
  )
}

function NeuesBuero({ schliessen }: { schliessen: () => void }) {
  const anlegen = useMandantAnlegen()

  return (
    <Blatt
      titel="Neues Prüfbüro"
      schliessen={schliessen}
      fuss={
        <div className="flex gap-3">
          <button type="button" className="knopf-still flex-1" onClick={schliessen}>
            Abbrechen
          </button>
          <button
            type="submit"
            form="buero"
            className="knopf-haupt flex-1"
            disabled={anlegen.isPending}
          >
            {anlegen.isPending ? 'Legt an …' : 'Anlegen'}
          </button>
        </div>
      }
    >
      <form
        id="buero"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          const f = new FormData(e.currentTarget)
          const text = (name: string) => String(f.get(name) ?? '').trim()
          anlegen.mutate(
            {
              name: text('name'),
              kurzform: text('kurzform').toUpperCase(),
              ort: text('ort') || null,
              inhaber_name: text('inhaber_name'),
              inhaber_email: text('inhaber_email'),
            },
            { onSuccess: schliessen },
          )
        }}
      >
        <div>
          <label htmlFor="m-name">Name des Prüfbüros</label>
          <input id="m-name" name="name" required maxLength={200} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="m-kurz">Kürzel</label>
            <input
              id="m-kurz"
              name="kurzform"
              required
              minLength={2}
              maxLength={20}
              pattern="[A-Za-z0-9-]+"
              placeholder="SPUER"
            />
            <p className="mt-1 text-base text-slate-600">
              Erscheint in Nummernkreisen. Nur Buchstaben, Ziffern und
              Bindestrich, und nur einmal vergeben.
            </p>
          </div>
          <div>
            <label htmlFor="m-ort">Ort</label>
            <input id="m-ort" name="ort" maxLength={100} />
          </div>
        </div>

        <div className="rounded-feld border-2 border-slate-200 p-3">
          <p className="font-semibold">Erstes Konto</p>
          <p className="mt-1 text-base text-slate-600">
            Bekommt die Rolle Inhaber und meldet sich über eine Einladung an.
            Ein Passwort, das wir hier setzen und weitergeben, stünde in zwei
            Postfächern.
          </p>

          <div className="mt-2 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="m-iname">Name</label>
              <input id="m-iname" name="inhaber_name" required maxLength={120} />
            </div>
            <div>
              <label htmlFor="m-imail">E-Mail</label>
              <input id="m-imail" name="inhaber_email" type="email" required />
              <p className="mt-1 text-base text-slate-600">
                Eine Adresse gehört zu genau einem Prüfbüro.
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-feld border-2 border-slate-200 bg-slate-50 p-3 text-base">
          Das neue Büro startet mit den Katalogen nach DIN — Prüfpunkte,
          Gerätetypen, Textbausteine und Prüfabläufe — und kann sie
          anschließend selbst umschreiben.
        </div>

        <Fehlerhinweis fehler={anlegen.error} />
      </form>
    </Blatt>
  )
}
